import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { API_CONFIG } from '../config/api';

interface AuthContextType {
  isAuthenticated: boolean | null;
  permissions: any[];
  setPermissions: (perms: any[]) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [permissions, setPermissions] = useState<any[]>(() => {
    try {
      const savedPermissions = localStorage.getItem('userPermissions');
      return savedPermissions ? JSON.parse(savedPermissions) : [];
    } catch (error) {
      return [];
    }
  });

  useEffect(() => {
    const verifyLogin = async () => {
      try {
        setIsAuthenticated(null);
        
        const response = await fetch(
          `${API_CONFIG.AUTH_URL}/verify`,
          {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
          }
        );

        setIsAuthenticated(response.ok);
      } catch (error) {
        console.error("Erro ao verificar autenticação", error);
        setIsAuthenticated(false);
      }
    };

    verifyLogin();

    const checkLogoutInterval = setInterval(() => {
      if (localStorage.getItem('logoutEvent')) {
        localStorage.removeItem('logoutEvent');
        setIsAuthenticated(false);
        setPermissions([]);
      }
    }, 500);

    return () => clearInterval(checkLogoutInterval);
  }, []);

  const setPermissionsWithStorage = (perms: any[]) => {
    setPermissions(perms);
    localStorage.setItem('userPermissions', JSON.stringify(perms));
  };

  const logout = () => {
    localStorage.clear();
    document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    
    localStorage.setItem('logoutEvent', 'true');    
    setIsAuthenticated(false);
    setPermissions([]);
    
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, permissions, setPermissions: setPermissionsWithStorage, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
